/// <reference types="@types/google.maps" />
import { ref, type Ref } from 'vue';

import {
  getAddressFromGeocoderAddressComponent,
  type GoogleAddress,
} from '@/utils/geocoderAddress.util';

import type { LibraryMap } from '@googlemaps/js-api-loader';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';

type AddressCallBack = (address: GoogleAddress) => void;

export function useLoadMaps() {
  const maps: Ref<typeof google.maps | null> = ref(null);

  const loadMaps = async (libraries: Array<keyof LibraryMap>) => {
    setOptions({
      key: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
      v: 'weekly',
    });

    const promises: Promise<unknown>[] = [];
    libraries.forEach(library => promises.push(importLibrary(library)));

    await Promise.all(promises);

    maps.value = window.google.maps;
  };

  const initAddressAutocomplete = (inputId: string, callback: AddressCallBack) => {
    if (!maps.value || !maps.value.places) {
      return;
    }

    const autocomplete = new maps.value.places.Autocomplete(
      document.getElementById(inputId) as HTMLInputElement,
      {
        componentRestrictions: { country: ['fr'] },
        fields: ['address_components', 'geometry'],
        types: ['address'],
      }
    );

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();

      if (!place) {
        return;
      }

      const fullAddress = getAddressFromGeocoderAddressComponent(
        place.address_components as google.maps.GeocoderAddressComponent[]
      );

      callback(fullAddress);
    });
  };

  return { loadMaps, initAddressAutocomplete };
}
